import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { v4 as uuidv4 } from 'uuid';
import produce from 'immer';
import { ConditionItemRender } from '@knowledge/knowledges/general/KnowledgeListForm/ConditionRenderer';
import checksvg from '@knowledge/assets/gouxuan.svg';
import { useLatest } from 'ahooks';
import { useGetKnowledgeText } from '@knowledge/knowledges/general/KnowledgeListForm/KnowledgeType';
import { groupBy } from './hook';
import { T } from '@tolgee/react';
import { knowledge2formValueMap } from 'views/BootGuide/views/AiModel/share';

type ResultSelectProps = {
  value: Knowledge.KnowledgeItem[];
  onChange: (val: {
    keep: Knowledge.KnowledgeItem[];
    overwrite: Knowledge.KnowledgeItem[];
  }) => void;
  contentValue: Knowledge.FormValue['contentValue'];
  type: Knowledge.KnowledgeType;
};

// checked 确认要的数据 selected当前框选的，需要做出状态改变的。
export type Value = Knowledge.KnowledgeItem & { uuid: string; checked: boolean; selected: boolean };

function ResultSelect(props: ResultSelectProps) {
  const { value: initialValue, onChange, contentValue, type } = props;
  const [originValue, setOriginValue] = useState<Value[]>(
    (initialValue || []).map((each) => {
      return {
        ...each,
        uuid: uuidv4(),
        selected: true,
      };
    }),
  );
  const value = useMemo(() => {
    return groupBy(originValue);
  }, [originValue]);

  const { getKnowledgeText } = useGetKnowledgeText();

  const knowledgeText = useMemo(() => {
    const formValue = knowledge2formValueMap[type]([], {
      ...(contentValue as any),
      value: contentValue.content,
    });
    return formValue ? getKnowledgeText(type, formValue) : '';
  }, [type, contentValue, getKnowledgeText]);

  const onChangeRef = useLatest(onChange);

  const changeSelected = useCallback((uuid: string) => {
    setOriginValue(
      produce((draft) => {
        draft.forEach((item) => {
          if (item.uuid === uuid) {
            item.selected = !item.selected;
          }
        });
      }),
    );
  }, []);

  useEffect(() => {
    onChangeRef.current?.({
      keep: originValue.filter((item) => !item.selected),
      overwrite: originValue.filter((item) => item.selected),
    });
  }, [originValue, onChangeRef]);

  return (
    <Container>
      {value.map((each) => {
        const formValue = knowledge2formValueMap[type]([], {
          templateCode: each.templateCode,
          value: each.content,
        });
        const curKnowledgeText = formValue ? getKnowledgeText(type, formValue) : '';
        return (
          <Box>
            <Header>
              <div className="flex items-start mb-[10px]">
                <div className="flex-shrink-0 mr-[8px] bg-[#FFF0F0] text-[12px] text-[var(--fett-surface-error-text)] leading-[24px] px-[8px]">
                  {
                    <T
                      keyName="knowledge.修改前x"
                      params={{
                        x: <></>,
                      }}
                    />
                  }
                </div>
                <div>{curKnowledgeText}</div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-[8px] bg-[var(--fett-surface-success-bg)] text-[12px] text-[var(--fett-scenario-success-text)] leading-[24px] px-[8px]">
                  {
                    <T
                      keyName="knowledge.修改后x"
                      params={{
                        x: <></>,
                      }}
                    />
                  }
                </div>
                <div>{knowledgeText}</div>
              </div>
            </Header>
            <Content>
              {each.conditions?.map((item) => (
                <ItemContainer
                  key={item.uuid}
                  selected={item.selected}
                  onClick={() => changeSelected(item.uuid)}
                >
                  <div className="px-[12px] py-[8px]">
                    <ConditionItemRender value={item} />
                  </div>
                </ItemContainer>
              ))}
            </Content>
          </Box>
        );
      })}
    </Container>
  );
}

const Container = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: calc(100% - 40px);
  overflow: auto;
`;

const Box = styled.div`
  border: 1px solid var(--fett-border-strong);
  width: 100%;
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const Header = styled.div`
  padding: 10px 8px;
  border-bottom: 1px solid var(--fett-border-strong);
`;

const Content = styled.div`
  padding: 14px 16px;
  background: var(--fett-erp-card-bg);
  display: flex;
  flex-direction: column;
`;

const ItemContainer = styled.div<{ selected: boolean }>`
  border-radius: 2px;
  border: 1px solid var(--fett-border-default);
  background: var(--fett-bg-panel);
  flex-shrink: 0;
  &:not(:last-child) {
    margin-bottom: 12px;
  }
  ${(props) =>
    props.selected &&
    `
    border-color: rgba(51,111,255,1);
    background-image: url(${`"${checksvg}"`});
    background-repeat: no-repeat;
    background-position: right bottom;
  `}
  &:hover {
    border: 1px solid rgba(51, 111, 255, 1);
  }
`;

export default ResultSelect;
